import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {TranslocoService} from '@ngneat/transloco';
import {AbstractComponent} from "../abstract.component";
import {
  ApplicationFormSettings,
  CategoryItem,
  Competitor, CompetitorFilter, CompetitorStatus, DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE,
  EventItem,
  FieldConfig,
  Lookups, PagedFilter, Pages, Paging,
  Race, RaceSettings,
  VisibilityTypes
} from "@model";
import {Sort} from "@utility";
import {CompetitorService, LoaderService, TOAST_STATE, ToastService} from "@services";
import {ActivatedRoute} from '@angular/router';

@Component({
  selector: 'app-competitor-table',
  templateUrl: './competitor-table.component.html',
  styleUrls: ['./competitor-table.component.scss']
})
export class CompetitorTableComponent extends AbstractComponent implements OnInit, Paging<Competitor> {
  public static readonly MAX_COLS_NUM: number = 10;
  maxColsNum: number = CompetitorTableComponent.MAX_COLS_NUM;
  page: number = DEFAULT_PAGE_INDEX;
  hasMore: boolean = false;
  isLoading: boolean = false;

  @Input() competitors: Competitor[] = [];
  @Input() race: Race | undefined;
  @Input() event: EventItem | undefined;
  @Input() isAdmin: boolean = false;
  @Input() showLoadMoreButton: boolean = false;
  @Input() showBorderBottom: boolean = true;
  @Input() genders: Lookups[] = [];
  @Input() categories: CategoryItem[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Input() filteringEnabled: boolean = true;
  @Input() showEmptyState: boolean = true;
  @Input() showHeader: boolean = false;
  @Input() displayRaceName: boolean = false;
  @Input() raceSettings: RaceSettings | undefined;
  @Input() raceSettingsList: RaceSettings[] = [];
  @Input() applicationFormSettingsList: ApplicationFormSettings[] = []
  @Input() applicationFormSettings: ApplicationFormSettings | undefined;
  @Input() isEventView: boolean = false;
  @Output() filterChange: EventEmitter<PagedFilter<CompetitorFilter>> = new EventEmitter<PagedFilter<CompetitorFilter>>();
  @Output() competitorListChanged: EventEmitter<Competitor[]> = new EventEmitter<Competitor[]>();

  name: string | undefined = "";
  gender: number | undefined = undefined;
  category: number | undefined = undefined;
  selectedStatus: CompetitorStatus | undefined;


  competitorStatuses: CompetitorStatus[] = [CompetitorStatus.REG, CompetitorStatus.BIB, CompetitorStatus.RDY,
    CompetitorStatus.STA, CompetitorStatus.FIN, CompetitorStatus.DSQ, CompetitorStatus.DNF, CompetitorStatus.TLE, CompetitorStatus.DNS];

  allowedVisibilityTypes: VisibilityTypes[] = [VisibilityTypes.ALWAYS_VISIBLE, VisibilityTypes.VISIBLE_TO_PUBLIC]
  availableFields: (keyof Competitor)[] = [...publicVisibleCompetitorFields];
  availableCustomFields: FieldConfig[] = [];
  competitorCols: (keyof Competitor)[] = [];
  defaultFields: (keyof Competitor)[] = [...publicVisibleCompetitorFields];
  customCols: FieldConfig[] = [];
  currentCompetitor: Competitor | undefined;

  selectedFields?: (keyof Competitor | number)[];

  constructor(private translocoService: TranslocoService, private competitorService: CompetitorService,
              private toastService: ToastService, private loaderService: LoaderService, private route: ActivatedRoute) {
    super();
  }

  ngOnInit(): void {
    this.handleExploreResult(this.competitors);
    if (this.isAdmin) {
      this.allowedVisibilityTypes = [VisibilityTypes.ALWAYS_VISIBLE, VisibilityTypes.VISIBLE_TO_ADMINISTRATION]
    }
    this.setAvailableFields();
    this.setDefaultFields();
    this.selectedFields = [...this.defaultFields]
    this.setTableColumns();

  }


  onSelectedFieldsChange(selectedFields: (keyof Competitor | number)[]) {
    if (!(selectedFields.length > 0)) {
      this.selectedFields = [...this.defaultFields];
    } else {
      this.selectedFields = [...selectedFields];
    }
    this.setTableColumns();
  }

  handleSearchInput(value: string): void {
    if (value === "") {
      this.name = undefined;
    } else {
      this.name = value;
    }
    this.onExplore(false);
  }

  handleGenderSelection(value?: any): void {
    if (value === this.gender || !value) {
      this.gender = undefined;
    } else {
      this.gender = value;
    }
    this.onExplore(false);
  }

  handleStatusSelection(status?: CompetitorStatus): void {
    this.selectedStatus = status;
    this.onExplore(false);
  }

  handleCategorySelection(value?: any): void {
    if (value === this.category || !value) {
      this.category = undefined;
    } else {
      this.category = value;
    }
    this.onExplore(false);
  }

  handleExploreResult(value: Competitor[], loadMore?: boolean): void {
    if (value) {
      if (value.length > DEFAULT_PAGE_SIZE) {
        value = (value.slice(0, DEFAULT_PAGE_SIZE))
        this.hasMore = true;
      } else {
        this.hasMore = false;
      }
      if (loadMore && this.competitors) {
        value = this.competitors.concat(value)
      }
    }
    this.setListOfItems(value);
  }

  setListOfItems(value: Competitor[] | null) {
    if (value && value.length > 0) {
      this.competitors = value;
      this.page++;
    } else {
      this.competitors = [];
    }
  }

  onExplore(loadMore?: boolean) {
    if (loadMore === true) {
      this.loaderService.startLoading();
    } else {
      this.page = 1;
      this.isLoading = true;
    }

    this.competitorService.getCompetitors(new PagedFilter(new Pages(undefined, this.page), this.getFilter())).subscribe((value: Competitor[]) => {
      if (loadMore === true) {
        this.loaderService.dismissLoading();
      } else {
        setTimeout(() => {
          this.isLoading = false;
        }, 300);
      }
      this.handleExploreResult(value, loadMore);
    });

  }

  loadMore(): void {
    this.onExplore(true);
  }

  private getFilter(): CompetitorFilter {
    let filter: CompetitorFilter = new CompetitorFilter();
    if (this.category) {
      filter.categories = [this.category];
    }
    filter.gender = this.gender ?? undefined;
    filter.name = this.name === '' ? undefined : this.name;
    filter.eventId = +this.route.snapshot.queryParams['eventId'];
    filter.raceId = this.race?.id;

    if (this.selectedStatus) {
      filter.competitorStatuses = [this.selectedStatus];
    }
    this.filterChange.emit(new PagedFilter(new Pages(undefined, this.page), filter));
    return filter;
  }

  private setTableColumns() {
    this.customCols = [];
    this.competitorCols = [];
    this.selectedFields?.forEach((value: keyof Competitor | number) => {
      if (typeof value === 'string') {
        this.competitorCols.push(value);
      } else if (typeof value === 'number') {
        const customField: FieldConfig | undefined = this.applicationFormSettings?.customFields?.filter((field: FieldConfig) => field.id === value).at(0);
        if (customField) {
          this.customCols.push(customField)
        }
      }
    })
    this.sortByDefault(this.competitorCols);
  }

  private setAvailableFields() {
    this.availableFields = []
    if (this.isAdmin) {
      this.availableFields = [...adminVisibleCompetitorFields]
      if (this.isEventView) {
        this.availableFields = [...this.availableFields, ...eventModeVisibleCompetitorFields]
      }
    } else {
      this.availableFields = [...publicVisibleCompetitorFields]
    }
    if (this.isEventView) {
      let availableFieldsSet: Set<(keyof Competitor)> = new Set();
      this.raceSettingsList.forEach((raceSettings: RaceSettings) => {
        if (raceSettings.blockStart) {
          availableFieldsSet.add("startBlock");
        }
      });
      this.applicationFormSettingsList.forEach((applicationFormSettings: ApplicationFormSettings) => {
        eventModeApplicationFormDependantCompetitorFields.forEach((visibility: keyof ApplicationFormSettings, competitorField: keyof Competitor) => {
          if (this.isApplicationFormFieldVisible(competitorField, applicationFormSettings)) {
            availableFieldsSet.add(competitorField);
          }
        });
      });
      this.availableFields = [...this.availableFields, ...Array.from(availableFieldsSet)];
      this.selectedFields = [...this.availableFields]
    } else {
      if (this.applicationFormSettings) {
        ApplicationFormDependantCompetitorFields.forEach((visibility: keyof ApplicationFormSettings, competitorField: keyof Competitor) => {
          if (this.isApplicationFormFieldVisible(competitorField, this.applicationFormSettings)) {
            this.availableFields.push(competitorField)
          }
        });
        this.applicationFormSettings.customFields?.forEach((customField: FieldConfig) => {
          if (this.isCustomFieldVisible(customField)) {
            this.availableCustomFields.push(customField);
          }
        })
      }
      if (this.raceSettings?.blockStart) {
        this.availableFields.push("startBlock");
      }
    }
  }

  private sortByDefault(arr: (keyof Competitor)[]) {
    const sortingArr = [...this.defaultFields, ...ApplicationFormDependantCompetitorFields.keys()];
    Sort.sortByOrder(arr, sortingArr);
  }

  private setDefaultFields() {
    if (this.isAdmin) {
      this.defaultFields = this.isEventView ? [...eventModeVisibleCompetitorFields] : [];
      if (this.isStartBlockUsed()) {
        this.defaultFields.push("startBlock");
      }
      this.defaultFields = [...this.defaultFields, ...adminVisibleCompetitorFields];
    } else {
      this.defaultFields = [...publicVisibleCompetitorFields]
    }
    this.sortByDefault(this.defaultFields);
  }

  isApplicationFormFieldVisible(fieldName: keyof Competitor, applicationFormSettings?: ApplicationFormSettings | undefined): boolean {
    if (!applicationFormSettings) {
      applicationFormSettings = this.applicationFormSettings;
    }
    const visibilityKey: keyof ApplicationFormSettings | undefined = ApplicationFormDependantCompetitorFields.get(fieldName)
    if (visibilityKey && applicationFormSettings) {
      const fieldVisibility: any = applicationFormSettings[visibilityKey]
      if (typeof fieldVisibility === "number") {
        return this.allowedVisibilityTypes.includes(fieldVisibility as VisibilityTypes);
      } else return false;
    } else {
      return false;
    }
  }

  isCustomFieldVisible(customField: FieldConfig): boolean {
    return !!customField.visibility && this.allowedVisibilityTypes.includes((customField.visibility));
  }

  onScroll(event: any): void {
    if (!this.showLoadMoreButton) {
      if (event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight) {
        if (this.hasMore) {
          this.onExplore(true);
        }
      }
    }
  }


  editCompetitor(competitor: Competitor) {
    if (this.isAdmin) {
      if (this.currentCompetitor?.id === competitor.id) {
        this.onCancel()
      } else {
        this.currentCompetitor = {...competitor};
        this.selectedFields = [];
        if (this.isStartBlockUsed(competitor)) {
          this.selectedFields.push("startBlock")
        }
        this.selectedFields = [...this.selectedFields, ...editModeVisibleCompetitorFields];
        this.setTableColumns();
      }
    }
  }

  onSave() {
    if (this.isAdmin && this.currentCompetitor) {
      this.selectedFields = [...this.defaultFields];
      this.setTableColumns();
      this.competitorService.manageCompetitor(this.currentCompetitor, (_: Competitor) => {
        this.onExplore(false);
      }, (errorCode?: number) => {
        this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate(
          "competitor.edit.error." + errorCode));
      });
      this.currentCompetitor = undefined;
    }
  }

  private isStartBlockUsed(competitor?: Competitor): boolean {
    if (this.isEventView) {
      const raceSettings: RaceSettings[] | undefined = competitor ?
        this.raceSettingsList.filter((raceSettings: RaceSettings) => raceSettings.raceId === competitor.raceId) :
        this.raceSettingsList.filter((raceSettings: RaceSettings) => raceSettings.blockStart);
      return !!((raceSettings?.length ?? 0) > 0 && raceSettings?.at(0)?.blockStart);
    } else {
      return !!this.raceSettings?.blockStart;
    }
  }

  onCancel() {
    if (this.isAdmin) {
      this.currentCompetitor = undefined;
      this.selectedFields = [...this.defaultFields];
      this.setTableColumns();
    }
  }

  deleteCompetitor(competitor: Competitor) {
    //todo
  }

  goToDetails(competitor: Competitor) {
    //todo
  }

  getOptions(): Map<string | number, string> {
    const optionsMap: Map<string | number, string> = new Map<string | number, string>();
    this.availableFields.map((option: (keyof Competitor)) => {
      optionsMap.set(option, this.translocoService.translate('competitor.table.' + option))
    });
    this.availableCustomFields.map((option: FieldConfig) => {
      if (option.id) {
        optionsMap.set(option.id, option.name ?? "");
      }
    });
    return optionsMap;
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  get tableClasses(): string {
    return `table align-middle header ${this.isAdmin ? 'dark-header' : 'table-striped'}`;
  }


}


export const adminVisibleCompetitorFields: (keyof Competitor)[] = [
  "startNumber",
  "fullName",
  "email",
  "status",
  "presentAtStart",
  "created",
  "modified",
  "birthday"
]

export const eventModeVisibleCompetitorFields: (keyof Competitor)[] = [
  "id",
]

export const editModeVisibleCompetitorFields: (keyof Competitor)[] = [
  "startNumber",
  "fullName",
  "status",
  "email",
  "presentAtStart"
];

export const publicVisibleCompetitorFields: (keyof Competitor)[] = [
  "startNumber",
  "fullName",
  "status",
  "email",
  "presentAtStart"
];

export const RaceSettingsDependantCompetitorFields: Map<keyof Competitor, keyof RaceSettings> = new Map([
  ["startBlock", "blockStart"]
]);

export const eventModeApplicationFormDependantCompetitorFields: Map<keyof Competitor, keyof ApplicationFormSettings> = new Map([
  ["sex", "genderVisibility"],
  ["country", "nationalityVisibility"],
  ["club", "clubVisibility"],
]);

export const ApplicationFormDependantCompetitorFields: Map<keyof Competitor, keyof ApplicationFormSettings> = new Map([
  ["sex", "genderVisibility"],
  ["country", "nationalityVisibility"],
  ["categoryLabel", "categoryRegistrationVisibility"],
  ["placeOfResidence", "cityVisibility"],
  ["address", "addressVisibility"],
  ["identityCardNumber", "identityCardNumberVisibility"],
  ["identityCardExpires", "identityCardExpiresVisibility"],
  ["identityCardIssuer", "identityCardIssuerVisibility"],
  ["club", "clubVisibility"],
  ["mobilePhone", "mobilePhoneVisibility"],
  ["tshirt", "tshirtVisibility"],
  ["meal", "mealVisibility"],
  ["transport", "transportVisibility"],
  ["comment", "commentVisibility"],
  ["chipNumber", "chipNumberVisibility"],
  ["bestTime", "bestTimeVisibility"],
  ["licence", "licenceVisibility"],
  ["emergencyPhone", "emergencyPhoneVisibility"],
  ["fee", "feeVisibility"]
]);


